// CE-04g-UI-chrome-1: canonical Position Workspace chart design tokens.
// TradeChart imports these constants instead of hardcoding literals. This is
// the single source of truth until the broader design-system migration.

import { LineStyle } from '../../lib/chart/createChartCompat'

// LWC createChart() options — workspace theme (default for the chassis).
// Canonical workspace-theme layout values.
export const CHART_LAYOUT_WORKSPACE = Object.freeze({
  background: { type: 'solid', color: 'transparent' },
  textColor: 'rgba(162,162,162,0.65)',
  fontSize: 11,
  fontFamily: "'DM Sans', sans-serif",
})

export const CHART_GRID_WORKSPACE = Object.freeze({
  vertLines: { visible: false },
  horzLines: { visible: false },
})

export const CHART_CROSSHAIR_WORKSPACE = Object.freeze({
  mode: 0,
  vertLine: {
    color: 'rgba(162,162,162,0.32)',
    style: 2,
    width: 1,
    labelBackgroundColor: 'rgba(38,38,38,0.92)',
  },
  horzLine: {
    color: 'rgba(162,162,162,0.32)',
    style: 2,
    width: 1,
    labelBackgroundColor: 'rgba(38,38,38,0.92)',
  },
})

export const CHART_RIGHT_PRICE_SCALE_MIN_WIDTH = 52

export const CHART_RIGHT_PRICE_SCALE_WORKSPACE = Object.freeze({
  borderColor: 'rgba(66,66,66,0.25)',
  textColor: 'rgba(162,162,162,0.65)',
  autoScale: true,
  mode: 1,
  // Compact floor for ordinary price labels. Volume values render inside
  // their pane and cannot widen the shared axis. Larger prices may expand it.
  minimumWidth: CHART_RIGHT_PRICE_SCALE_MIN_WIDTH,
})

export const CHART_TIME_SCALE_WORKSPACE = Object.freeze({
  // Layout changes enlarge the current view instead of exposing different candles.
  lockVisibleTimeRangeOnResize: true,
  visible: true,
  borderVisible: true,
  borderColor: 'rgba(66,66,66,0.25)',
  secondsVisible: false,
})

function isLightDocumentTheme() {
  return typeof document !== 'undefined' && !document.documentElement.classList.contains('dark')
}

// Candle / bar series colors (workspace theme).
// Canonical workspace candle and bar colors.
export const SERIES_COLORS_WORKSPACE = Object.freeze({
  up: '#4aaa6a',
  down: '#d45454',
  get barUp() {
    return isLightDocumentTheme() ? this.barUpLight : '#D1D5DB'
  },
  get barDown() {
    return isLightDocumentTheme() ? this.barDownLight : '#D1D5DB'
  },
  barUpLight: '#000000',
  barDownLight: '#000000',
})

// Price-line colors (workspace theme).
// Canonical workspace price-line colors.
export const PRICE_LINE_COLORS_WORKSPACE = Object.freeze({
  avgCostDark: 'rgba(255,255,255,0.5)',
  avgCostLight: 'rgba(107,114,128,0.6)',
  stop: 'rgba(224,90,74,0.7)',
  stopGlow: Object.freeze([255, 80, 100]),
})

// Pill button styling — used by ChartTopBar.
export const PILL_CLASS = Object.freeze({
  base: 'font-mono text-xs py-1 px-2.5 rounded-full transition-colors',
  active: 'bg-canvas2 border border-border text-ink font-medium',
  inactive: 'border border-transparent text-inkMuted hover:text-ink hover:bg-canvas2',
})

// Tighter pill variant used by the TF group only (CE-04g-UI-chrome-1-fix-2
// item C). Lower horizontal padding packs the 12 timeframe pills closer
// together so the row reads as a single dense control, TradingView-style.
// Other groups (Candles/Bars, Indicators) keep the standard PILL_CLASS.
export const PILL_CLASS_TIGHT = Object.freeze({
  base: 'font-mono text-xs py-1 px-1.5 rounded-full transition-colors',
  active: 'bg-canvas2 border border-border text-ink font-medium',
  inactive: 'border border-transparent text-inkMuted hover:text-ink hover:bg-canvas2',
})

// Top-bar container styling.
// Hide native scrollbar space as well as its WebKit paint; a thin scrollbar
// otherwise consumes the fixed-height row when controls overflow.
export const TOP_BAR_CONTAINER_CLASS =
  'px-3 py-1.5 border-b border-border flex items-center gap-1 shrink-0 h-10 min-w-0 overflow-x-auto touch-pan-x [&>*]:shrink-0 hide-scrollbar'

// Chart frame container (wraps top bar + canvas).
export const CHART_FRAME_CLASS =
  'bg-canvas border border-border rounded-lg overflow-hidden flex flex-col isolate'

// Pill-group separator (vertical hairline between pill groups in the top bar).
export const PILL_SEPARATOR_CLASS = 'w-px h-4 bg-border mx-2'

// Quiet chart-internal borders: price scale, time scale and volume separator.
// Outer frame and toolbar borders retain their existing design tokens.
export const BORDER_COLOR_RGBA = 'rgba(66,66,66,0.25)'
export const BORDER_COLOR_RGBA_HOVER = 'rgba(128,128,128,0.12)'

// Mobile breakpoints. Tailwind defaults: sm=640, md=768, lg=1024.
export const BREAKPOINTS = Object.freeze({
  sm: 640,
  md: 768,
  lg: 1024,
})

export { LineStyle }
